import {IV_DOCUMENT_IMAGE_TYPES} from '../utils/constants';
import {apiClient, FetcherSettings} from './apiClient';
import {IMAGE_SERVICE_ORIGIN, IV_PHOTO_SOURCE} from '../utils';
import {DocumentChoice} from '../services';

export type FinishIVByQrPayload = {
  iv_intent_id: string;
  continue_on_mobile: boolean;
};

export type IVPayload = {
  id?: string;
  iv_intent_id?: string;
  source?: IV_PHOTO_SOURCE;
  picture_file?: string;
  for_country?: string;
  reservation_id?: string;
  type_doc?: string;
  nationality?: string;
};

enum IVStatus {
  complete = 'COM',
  error = 'ERR',
  new = 'NEW',
  process = 'PRO',
}

export type BiomatchResponse = {
  id: string;
  is_face_detected: boolean;
  picture_file: string;
  reservation_id: string;
  status: IVStatus;
  user_email: string;
  source: IV_PHOTO_SOURCE;
};

export type OCRDetectedData = {
  country: string | null;
  created_by: string;
  date_of_birth: string | null;
  do_face_extraction: boolean;
  expiration_date: string | null;
  extracted_face_file: string | null;
  first_surname: string;
  id: string;
  issue_date: string;
  mrz_detected: boolean;
  barcode_detected: boolean;
  names: string | null;
  nationality: string | null;
  number: string | null;
  picture_file: string;
  picture_with_face: 1 | 0;
  place_of_birth: string | null;
  sex: 'M' | 'F' | null;
  status: IVStatus;
  surname: string | null;
  type_doc: string | null;
  valid_composite: boolean;
  valid_date_of_birth: boolean;
  support_number: string | null;
  valid_expiration_date: boolean;
  valid_issue_date: boolean;
  valid_number: boolean;
  valid_score: number | null;
  is_face_detected: boolean;
  second_surname: string;
  source: IV_PHOTO_SOURCE;
};

export type CompareBiomatchResponse = {
  distance: number | null;
  guest_name: string | null;
  id: string;
  identification_picture: string;
  is_match: false;
  person_picture: string;
  reservation_creation_date: Date | string | null;
  reservation_id: string;
  status: IVStatus;
  user_email: string;
};

export type CompareBiomatchPayload = {
  reservation_id: string;
  identification_picture: string;
  person_picture: string;
};

export type ImageDetails = {
  id?: string;
  image: string | null;
  uploaded?: boolean;
  type: IV_DOCUMENT_IMAGE_TYPES;
  mrzDetected?: boolean;
  barcodeDetected?: boolean;
  faceDetected?: boolean;
  service?: IMAGE_SERVICE_ORIGIN;
  extraID?: string;
};

export type IVIntent = {
  id: string;
  reservation_id: string;
  mrz?: OCRDetectedData;
  biomatch_passed?: boolean;
  document_passed?: boolean;
  nationality?: string;
  type_doc?: DocumentChoice;
  image_details?: {
    [key in IV_DOCUMENT_IMAGE_TYPES]?: Partial<ImageDetails> & {
      type: IV_DOCUMENT_IMAGE_TYPES;
    };
  };
};

export type CreateIvIntentPayload = {
  id: string;
  reservation_id: string;
  user_id: string;
};

export type UpdateIvIntentPayload = Omit<Partial<IVIntent>, 'id'> & Pick<IVIntent, 'id'>;

class IdentityVerificationApi {
  private readonly ENDPOINTS = {
    biomatchIdentification: (id = '') =>
      `api/v4/biomatch/identification/${id ? `${id}/` : ``}`,
    biomatchPerson: (id = '') => `api/v4/biomatch/person/${id ? `${id}/` : ``}`,
    biomatchCompare: (id = '') => `api/v4/biomatch/compare/${id ? `${id}/` : ``}`,
    ocrPicture: () => `api/v3/ocr/picture/`,
    ocrData: (id = '') => `api/v3/ocr/data/${id ? `${id}/` : ``}`,
    ivIntent: (id = '') => `api/v3/ocr/iv-intent/${id ? `${id}/` : ``}`,
    startIVOnMobile: () => `api/v3/ocr/process-iv-by-qr/start-iv-on-mobile`,
    finishIVByQr: () => 'api/v3/ocr/process-iv-by-qr/finish-iv-by-qr',
  };

  async sendDocumentForFaceDetection(
    payload: IVPayload,
    fetcherSettings?: FetcherSettings,
  ) {
    const response = await apiClient.post<BiomatchResponse>(
      this.ENDPOINTS.biomatchIdentification(),
      {
        json: payload,
        ...fetcherSettings,
      },
    );
    return response.body;
  }

  async checkDocumentFaceDetectionStatus(id: string, fetcherSettings?: FetcherSettings) {
    const response = await apiClient.get<BiomatchResponse>(
      this.ENDPOINTS.biomatchIdentification(id),
      fetcherSettings,
    );
    return response.body;
  }

  async sendSelfieForFaceDetection(
    payload: IVPayload,
    fetcherSettings?: FetcherSettings,
  ) {
    const response = await apiClient.post<BiomatchResponse>(
      this.ENDPOINTS.biomatchPerson(),
      {
        json: payload,
        ...fetcherSettings,
      },
    );
    return response.body;
  }

  async checkSelfieFaceDetection(id: string, fetcherSettings?: FetcherSettings) {
    const response = await apiClient.get<BiomatchResponse>(
      this.ENDPOINTS.biomatchPerson(id),
      fetcherSettings,
    );
    return response.body;
  }

  async compareSelfieAndDocumentFaces(
    payload: CompareBiomatchPayload,
    fetcherSettings?: FetcherSettings,
  ) {
    const response = await apiClient.post<CompareBiomatchResponse>(
      this.ENDPOINTS.biomatchCompare(),
      {
        json: payload,
        ...fetcherSettings,
      },
    );
    return response.body;
  }

  async checkSelfieAndDocumentFacesComparingStatus(
    id = '',
    fetcherSettings?: FetcherSettings,
  ) {
    const response = await apiClient.get<CompareBiomatchResponse>(
      this.ENDPOINTS.biomatchCompare(id),
      fetcherSettings,
    );
    return response.body;
  }

  async sendImageForOCR(payload: IVPayload, fetcherSettings?: FetcherSettings) {
    const response = await apiClient.post<OCRDetectedData>(this.ENDPOINTS.ocrPicture(), {
      json: payload,
      ...fetcherSettings,
    });
    return response.body;
  }

  async checkImageOCRStatus(id = '', fetcherSettings?: FetcherSettings) {
    const response = await apiClient.get<OCRDetectedData>(
      this.ENDPOINTS.ocrData(id),
      fetcherSettings,
    );
    return response.body;
  }

  async createIvIntent(
    payload: CreateIvIntentPayload,
    fetcherSettings?: FetcherSettings,
  ) {
    const response = await apiClient.post<IVIntent, CreateIvIntentPayload>(
      this.ENDPOINTS.ivIntent(),
      {
        json: payload,
        ...fetcherSettings,
      },
    );
    return response.body;
  }

  async updateIvIntent(
    payload: UpdateIvIntentPayload,
    fetcherSettings?: FetcherSettings,
  ) {
    const response = await apiClient.patch<IVIntent, UpdateIvIntentPayload>(
      this.ENDPOINTS.ivIntent(payload.id),
      {
        json: payload,
        ...fetcherSettings,
      },
    );
    return response.body;
  }

  async getIvIntent(id: string, fetcherSettings?: FetcherSettings) {
    const response = await apiClient.get<IVIntent>(
      this.ENDPOINTS.ivIntent(id),
      fetcherSettings,
    );
    return response.body;
  }

  async startIVOnMobile(id: string, fetcherSettings?: FetcherSettings) {
    const response = await apiClient.post<IVIntent>(this.ENDPOINTS.startIVOnMobile(), {
      json: {
        iv_intent_id: id,
      },
      ...fetcherSettings,
    });
    return response.body;
  }

  async finishIVByQr(payload: FinishIVByQrPayload, fetcherSettings?: FetcherSettings) {
    const response = await apiClient.post<IVIntent>(this.ENDPOINTS.finishIVByQr(), {
      json: payload,
      ...fetcherSettings,
    });
    return response.body;
  }
}

export {IdentityVerificationApi, IVStatus};
